import {
  ClockIcon,
  IconButton,
  SettingsIcon,
  SingleSelectDropdown,
} from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import {
  canManageSchedules,
  canViewScheduleOperations,
  SCHEDULE_OPERATIONS_ROLES,
  useAppState,
} from "@/app";
import { hatcheryDropdownModes } from "@/app/theme";
import { GnbMenu } from "@/components/base";
import { FilterBar, type FilterBarChip } from "@/components/filter";
import { PageBase, type PageBaseContext, type PageBaseProps } from "@/page/base/PageBase";
// 배럴(@/page/training) 대신 모듈을 직접 가져온다. 배럴은 TrainingPage 계열 전체를 가져온다.
import { openTrainingScheduleWindow } from "@/page/training/trainingSubmitWindow";
import type { TrainingScheduleOperationsItem } from "@/api/trainingSchedule";
import { ScheduleEditDialog } from "./ScheduleEditDialog";
import {
  openScheduleOccurrencesWindow,
  syncScheduleOccurrencesWindow,
} from "./scheduleOccurrencesWindow";
import { ScheduleRecipePanel } from "./ScheduleRecipePanel";
import { SharedWorkspaceModal } from "./SharedWorkspaceModal";
import { SchedulePanel } from "./SchedulePanel";
import {
  DEFAULT_SCHEDULE_STATUS_FILTER,
  normalizeScheduleStatusFilter,
  SCHEDULE_STATUS_FILTERS,
  SCHEDULE_STATUS_PARAM,
  scheduleStatusFilterLabel,
  serializeScheduleStatusFilter,
  type ScheduleStatusFilter,
} from "./scheduleFilters";
import {
  useTrainingSchedules,
  type TrainingScheduleCommand,
  type TrainingSchedulesController,
} from "./useTrainingSchedules";

/**
 * Operations > Schedule. 공용 workspace 에서 실행되는 반복 학습 제출의 전역 목록이다.
 *
 * 목록, 조회 축(status), 자격은 이 컴포넌트가 조립하고, 표는 SchedulePanel 이 그린다.
 * status 축 상태는 URL 이 소유한다(rules §17).
 */
export function SchedulePage(): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = normalizeScheduleStatusFilter(searchParams.get(SCHEDULE_STATUS_PARAM));
  const { state } = useAppState();
  const role = state.currentUser?.role ?? null;
  const permissions = state.currentUser?.permissions ?? null;
  // 자격이 없는 사람의 목록 요청(403)을 보내지 않는다. 게이트는 PageBase 가 본문을 막고,
  // 여기서는 조회를 끈다.
  const canView = canViewScheduleOperations(role, permissions);
  const controller = useTrainingSchedules(role, canView);
  // 편집 대상 행(null = 닫힘). 다이얼로그는 페이지 로컬 모달이라 창 스택을 쓰지 않는다.
  const [editing, setEditing] = useState<TrainingScheduleOperationsItem | null>(null);
  // 그룹 공용 Workspace 관리 모달. 페이지 로컬이다. 닫아도 목록은 다시 받지 않는다(목록과 무관한 축).
  const [sharedWorkspacesOpen, setSharedWorkspacesOpen] = useState(false);
  // Recipe 패널 대상 행(null = 닫힘). 페이지 로컬 side panel 이라 창 스택을 쓰지 않는다.
  const [recipeTarget, setRecipeTarget] = useState<TrainingScheduleOperationsItem | null>(null);
  // 패널이 열린 동안 목록이 새 버전을 받으면 제목과 상태 칩은 최신 행을 따른다. 다시 받는 중이거나
  // 목록에서 빠졌으면 연 시점의 행을 그대로 쓴다.
  const recipeSchedule =
    recipeTarget === null || controller.state.status !== "success"
      ? recipeTarget
      : (controller.state.data.find((item) => item.id === recipeTarget.id) ?? recipeTarget);

  // 목록이 새 버전을 받을 때마다(명령 후 행 교체·저장 후 재적재) 열린 이력 창의 스냅샷도 맞춘다.
  useEffect(() => {
    if (controller.state.status === "success") {
      controller.state.data.forEach(syncScheduleOccurrencesWindow);
    }
  }, [controller.state]);

  // 일시정지와 재개는 다이얼로그를 연 채 상태만 바꾼다. 응답 view 로 열린 행을 덮는다.
  // 목록은 controller 가 다시 받지만 그동안 loading 이다. 그래서 열린 행의 원본은 이 스냅샷이다.
  const runEditingCommand = useCallback(
    async (command: TrainingScheduleCommand): Promise<void> => {
      if (editing === null) {
        return;
      }
      const updated = await controller.run(command, editing.id);
      if (updated !== null) {
        setEditing((prev) => (prev === null ? null : { ...prev, ...updated }));
      }
    },
    [controller, editing],
  );

  const onStatusFilterChange = useCallback(
    (next: ScheduleStatusFilter) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        const serialized = serializeScheduleStatusFilter(next);
        if (serialized === null) {
          params.delete(SCHEDULE_STATUS_PARAM);
        } else {
          params.set(SCHEDULE_STATUS_PARAM, serialized);
        }
        return params;
      });
    },
    [setSearchParams],
  );

  return (
    <>
      <SchedulePageView
        pageTitle="Schedule"
        pageTitleTooltip="공용 Workspace에서 반복 실행되는 Training 제출을 관리합니다."
        currentMenu={GnbMenu.TrainingSchedule}
        requiredRoles={SCHEDULE_OPERATIONS_ROLES}
        orPermission="can_use_schedule_operations"
        bodyFlushTop
        controller={controller}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        onEdit={setEditing}
        onOpenRecipe={setRecipeTarget}
        onOpenSharedWorkspaces={() => setSharedWorkspacesOpen(true)}
      />
      {sharedWorkspacesOpen ? (
        <SharedWorkspaceModal onClose={() => setSharedWorkspacesOpen(false)} />
      ) : null}
      {recipeSchedule === null ? null : (
        <ScheduleRecipePanel schedule={recipeSchedule} onClose={() => setRecipeTarget(null)} />
      )}
      {editing === null ? null : (
        <ScheduleEditDialog
          item={editing}
          role={role}
          canManage={canManageSchedules(permissions)}
          onCommand={runEditingCommand}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

type SchedulePageViewProps = PageBaseProps & {
  controller: TrainingSchedulesController;
  statusFilter: ScheduleStatusFilter;
  onStatusFilterChange: (next: ScheduleStatusFilter) => void;
  onEdit: (item: TrainingScheduleOperationsItem) => void;
  onOpenRecipe: (item: TrainingScheduleOperationsItem) => void;
  onOpenSharedWorkspaces: () => void;
};

class SchedulePageView extends PageBase<SchedulePageViewProps> {
  // 쓰기 자격(admin)이 있을 때만 두 버튼을 표시한다. research 열람자에게는 그 자리가 없다. 공용
  // Workspace 관리가 왼쪽(surface), 새 Schedule 이 오른쪽(solid)이다. Schedule 은 그 Workspace 에서 만든다.
  protected override renderHeaderActions(context: PageBaseContext): ReactNode {
    if (!canManageSchedules(context.currentUserPermissions)) {
      return null;
    }
    return (
      <>
        <IconButton
          text="그룹 공용 Workspace 관리"
          variant="surface"
          onClick={this.props.onOpenSharedWorkspaces}
        >
          <SettingsIcon />
        </IconButton>
        <IconButton text="새 Schedule" variant="solid" onClick={openTrainingScheduleWindow}>
          <ClockIcon />
        </IconButton>
      </>
    );
  }

  protected override renderBelowTabs(): ReactNode {
    const { statusFilter, onStatusFilterChange } = this.props;
    const chips: FilterBarChip[] =
      statusFilter === DEFAULT_SCHEDULE_STATUS_FILTER
        ? []
        : [
            {
              key: "status",
              label: `상태: ${scheduleStatusFilterLabel(statusFilter)}`,
              removeLabel: "상태 조건 지우기",
              onRemove: () => onStatusFilterChange(DEFAULT_SCHEDULE_STATUS_FILTER),
            },
          ];
    return (
      <FilterBar
        chips={chips}
        onClearAll={() => onStatusFilterChange(DEFAULT_SCHEDULE_STATUS_FILTER)}
        ariaLabel="Schedule 목록 필터"
      >
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.filter}
          label="상태"
          neutralValue={DEFAULT_SCHEDULE_STATUS_FILTER}
          value={statusFilter}
          options={SCHEDULE_STATUS_FILTERS.map((filter) => ({
            value: filter,
            label: scheduleStatusFilterLabel(filter),
          }))}
          onValueChange={(next) => onStatusFilterChange(next as ScheduleStatusFilter)}
        />
      </FilterBar>
    );
  }

  protected override renderContent(context: PageBaseContext): ReactNode {
    const { controller, statusFilter, onEdit, onOpenRecipe } = this.props;
    return (
      <SchedulePanel
        controller={controller}
        statusFilter={statusFilter}
        canManage={canManageSchedules(context.currentUserPermissions)}
        onEdit={onEdit}
        onOpenHistory={openScheduleOccurrencesWindow}
        onOpenRecipe={onOpenRecipe}
      />
    );
  }
}
